import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Shield, Target, FileText, Users, ArrowRight, CheckCircle2, BarChart3, Brain, Send, Lock } from 'lucide-react';

const Landing = () => {
  return (
    <div className="min-h-screen bg-[#f7f6f0] text-stone-800">
      <nav className="border-b border-stone-200/80 bg-[#f7f6f0]/90 backdrop-blur sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-[#4b5320] flex items-center justify-center rounded-sm">
              <Shield className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <div className="font-display text-lg tracking-wider">
              INFANTRY <span className="text-[#4b5320]">/ DISPUTE</span>
            </div>
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm text-stone-700">
            <a href="#features" className="hover:text-stone-900 transition-colors">Platform</a>
            <a href="#how" className="hover:text-stone-900 transition-colors">How it works</a>
            <Link to="/pricing" className="hover:text-stone-900 transition-colors">Pricing</Link>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm text-stone-700 hover:text-stone-900">Sign in</Link>
            <Link to="/signup">
              <Button className="bg-[#4b5320] hover:bg-[#3d4419] text-white font-medium rounded-sm">Get Started</Button>
            </Link>
          </div>
        </div>
      </nav>

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 diagonal-stripes opacity-40"></div>
        <div className="max-w-7xl mx-auto px-6 py-24 md:py-32 relative">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#4b5320]/40 bg-[#4b5320]/10 rounded-sm mb-6">
              <div className="w-1.5 h-1.5 bg-[#4b5320] rounded-full pulse-gold"></div>
              <span className="text-xs uppercase tracking-widest text-[#4b5320] font-medium">For Credit Repair Organizations</span>
            </div>
            <h1 className="font-display text-5xl md:text-7xl font-semibold leading-[1.05] tracking-tight">
              THE DISPUTE PLATFORM<br/>
              <span className="text-[#4b5320]">BUILT WITH MILITARY</span><br/>
              PRECISION.
            </h1>
            <p className="mt-8 text-lg text-stone-700 max-w-2xl leading-relaxed">
              Streamline how your credit repair organization onboards clients, collects documents, analyzes credit reports with AI, and ships disputes — all in one disciplined workflow.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/signup">
                <Button size="lg" className="bg-[#4b5320] hover:bg-[#3d4419] text-white font-semibold rounded-sm h-12 px-6">
                  Get Started <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button size="lg" variant="outline" className="border-stone-300 bg-transparent hover:bg-stone-100 hover:border-stone-300 text-stone-800 rounded-sm h-12 px-6">
                  See pricing — $6.99/mo per client
                </Button>
              </Link>
            </div>
            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-2xl">
              {[
                { v: '500+', l: 'CROs onboard' },
                { v: '12K+', l: 'Disputes shipped' },
                { v: '94%', l: 'Removal rate' },
                { v: 'CROA', l: 'Compliant' },
              ].map((s, i) => (
                <div key={i} className="border-l-2 border-[#4b5320] pl-4">
                  <div className="font-display text-3xl text-stone-900">{s.v}</div>
                  <div className="text-xs uppercase tracking-widest text-stone-500 mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="features" className="border-t border-stone-200/80 py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-px bg-[#4b5320]"></div>
            <span className="text-xs uppercase tracking-widest text-[#4b5320] font-medium">The Platform</span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl mb-4">Everything you need to dispute.</h2>
          <p className="text-stone-700 max-w-2xl mb-16">Stop juggling spreadsheets, PDF templates, and DocuSign. Run your entire dispute operation from one place.</p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-stone-200/60">
            {[
              { icon: Users, title: 'Client Onboarding', desc: 'Capture client info, ID, and consent forms in minutes. CROA-compliant intake out of the box.' },
              { icon: FileText, title: 'Document Collection', desc: 'Secure portal for clients to upload credit reports, IDs, and proof of address. No more email back-and-forth.' },
              { icon: Brain, title: 'AI Analysis', desc: 'Upload a credit report and our AI surfaces every disputable item with confidence scoring and suggested grounds.' },
              { icon: Send, title: 'Dispute Letters', desc: 'Generate FCRA-compliant dispute letters for all three bureaus with one click. PDF-ready, mail-ready.' },
              { icon: BarChart3, title: 'Progress Tracking', desc: 'Watch scores climb. Track items removed across rounds and bureaus with crisp dashboards.' },
              { icon: Lock, title: 'CROA Compliant', desc: 'Built-in safeguards keep your organization on the right side of state and federal regulations.' },
            ].map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="bg-[#f7f6f0] p-8 hover:bg-[#fafaf6] transition-colors">
                  <div className="w-11 h-11 border border-[#4b5320]/40 bg-[#4b5320]/10 flex items-center justify-center rounded-sm mb-5">
                    <Icon className="w-5 h-5 text-[#4b5320]" strokeWidth={1.8} />
                  </div>
                  <h3 className="font-display text-xl mb-2 tracking-wide">{f.title}</h3>
                  <p className="text-sm text-stone-700 leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section id="how" className="border-t border-stone-200/80 py-24 bg-[#ffffff]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-px bg-[#4b5320]"></div>
            <span className="text-xs uppercase tracking-widest text-[#4b5320] font-medium">The Process</span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl mb-16">From client to dispute in 4 steps.</h2>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { n: '01', t: 'Add Your Client', d: 'Enter client info and send them a secure invite to upload documents.' },
              { n: '02', t: 'Collect Reports', d: 'Client uploads their three-bureau credit reports and ID — encrypted at rest.' },
              { n: '03', t: 'AI Analyzes', d: 'Our model identifies every disputable item with FCRA-grade reasoning.' },
              { n: '04', t: 'Ship Disputes', d: 'Generate, review, and mail letters. Track responses round by round.' },
            ].map((s, i) => (
              <div key={i} className="relative">
                <div className="font-display text-6xl text-[#4b5320]/30">{s.n}</div>
                <div className="w-12 h-px bg-[#4b5320] my-4"></div>
                <h3 className="font-display text-xl mb-2 tracking-wide">{s.t}</h3>
                <p className="text-sm text-stone-700 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-stone-200/80 py-24 relative overflow-hidden">
        <div className="absolute inset-0 diagonal-stripes opacity-30"></div>
        <div className="max-w-4xl mx-auto px-6 text-center relative">
          <Target className="w-12 h-12 text-[#4b5320] mx-auto mb-6" strokeWidth={1.5} />
          <h2 className="font-display text-4xl md:text-6xl mb-6">READY TO DISPUTE WITH PRECISION?</h2>
          <p className="text-stone-700 max-w-2xl mx-auto mb-10">Join credit repair organizations running disciplined dispute operations on Infantry. Just $6.99/month per active client — no setup fees, no tiers.</p>          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/signup">
              <Button size="lg" className="bg-[#4b5320] hover:bg-[#3d4419] text-white font-semibold rounded-sm h-12 px-8">
                Get Started Today
              </Button>
            </Link>
            <Link to="/pricing">
              <Button size="lg" variant="outline" className="border-stone-300 bg-transparent hover:bg-stone-100 text-stone-800 rounded-sm h-12 px-8">
                View pricing
              </Button>
            </Link>
          </div>
          <div className="mt-10 flex flex-wrap justify-center gap-6 text-sm text-stone-500">
            <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#4b5320]" /> No credit card required</span>
            <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#4b5320]" /> Cancel anytime</span>
            <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#4b5320]" /> CROA compliant</span>
          </div>
        </div>
      </section>

      <footer className="border-t border-stone-200/80 py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-wrap items-center justify-between gap-4 text-sm text-stone-500">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 bg-[#4b5320] flex items-center justify-center rounded-sm">
              <Shield className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display tracking-wider text-stone-700">INFANTRY / DISPUTE</span>
          </div>
          <div>&copy; {new Date().getFullYear()} Infantry Dispute. Built for credit repair professionals.</div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
